import { create } from 'zustand'
import { toast } from 'sonner'
import InstancesService from '@/services/instances.service'
import type { Instance } from '@/types'

interface InstancesStore {
  instances: Instance[]
  selectedInstance: Instance | null
  isLoading: boolean
  fetchInstances: () => Promise<void>
  setSelected: (instance: Instance | null) => void
  updateInstance: (instanceId: string, updates: Partial<Instance>) => void
  updateInstanceStatus: (
    instanceId: string,
    status: Instance['status'],
    qrCodeData?: string,
  ) => void
  addInstance: (instance: Instance) => void
  removeInstance: (instanceId: string) => void
}

export const useInstancesStore = create<InstancesStore>((set, get) => ({
  instances: [],
  selectedInstance: null,
  isLoading: false,

  fetchInstances: async () => {
    set({ isLoading: true })
    try {
      const instances = await InstancesService.getAll()
      set((state) => ({
        instances,
        selectedInstance: state.selectedInstance
          ? instances.find((instance) => instance.id === state.selectedInstance?.id) ?? null
          : null,
      }))
    } catch {
      toast.error('Erro ao carregar instâncias.')
    } finally {
      set({ isLoading: false })
    }
  },

  setSelected: (instance: Instance | null) => {
    set({ selectedInstance: instance })
  },

  updateInstance: (instanceId, updates) => {
    set((state) => ({
      instances: state.instances.map((instance) =>
        instance.id === instanceId
          ? { ...instance, ...updates }
          : instance,
      ),
      selectedInstance:
        state.selectedInstance?.id === instanceId
          ? {
              ...state.selectedInstance,
              ...updates,
            }
          : state.selectedInstance,
    }))
  },

  updateInstanceStatus: (
    instanceId: string,
    status: Instance['status'],
    qrCodeData?: string,
  ) => {
    get().updateInstance(instanceId, {
      status,
      ...(qrCodeData !== undefined && { qr_code_data: qrCodeData }),
    })

    const { instances } = get()
    const instance = instances.find((i) => i.id === instanceId)
    if (instance) {
      if (status === 'connected') {
        toast.success(`Instância "${instance.name}" conectada!`)
      } else if (status === 'disconnected') {
        toast.warning(`Instância "${instance.name}" desconectada.`)
      }
    }
  },

  addInstance: (instance: Instance) => {
    set((state) => ({
      instances: [instance, ...state.instances.filter((item) => item.id !== instance.id)],
    }))
  },

  removeInstance: (instanceId: string) => {
    set((state) => ({
      instances: state.instances.filter((i) => i.id !== instanceId),
      selectedInstance:
        state.selectedInstance?.id === instanceId
          ? null
          : state.selectedInstance,
    }))
  },
}))
